import React, { useState } from 'react';
import { Calendar, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { validateOrders } from '../services/api';

interface ValidationResult {
  success: boolean;
  message: string;
  processed: number;
  errors: number;
  total: number;
}

const OrderValidation: React.FC = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [result, setResult] = useState<ValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleValidation = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date cannot be after end date');
      return;
    }

    setIsValidating(true);
    setError(null);
    setResult(null);

    try {
      // Format dates to YYYY-MM-DD format for the API
      const formattedStartDate = startDate;
      const formattedEndDate = endDate;
      
      const response = await validateOrders(formattedStartDate, formattedEndDate);
      setResult(response);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to validate orders. Please try again.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleClear = () => {
    setStartDate('');
    setEndDate('');
    setResult(null);
    setError(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Order Validation</h2>
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <Calendar className="w-4 h-4" />
          <span>Validate payment status for orders within a date range</span>
        </div>
      </div>

      {/* Date Range Selection */}
      <div className="card">
        <div className="mb-4">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Select Date Range</h3>
          <p className="text-sm text-gray-600 mb-4">
            This will validate payment status for all orders with 'INITIATED' status within the selected date range.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="input-field"
              max={endDate || undefined}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="input-field"
              min={startDate || undefined}
            />
          </div>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={handleValidation}
            disabled={isValidating || !startDate || !endDate}
            className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isValidating ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                <span>Validating...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                <span>Validate Orders</span>
              </>
            )}
          </button>
          
          <button
            onClick={handleClear}
            disabled={isValidating}
            className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="card border-red-200 bg-red-50">
          <div className="flex items-center space-x-2 text-red-800">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">{error}</span>
          </div>
        </div>
      )}

      {/* Results Display */}
      {result && (
        <div className="card border-green-200 bg-green-50">
          <div className="flex items-center space-x-2 text-green-800 mb-4">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">{result.message}</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <div className="text-2xl font-bold text-green-600">{result.total}</div>
              <div className="text-sm text-gray-600">Total Orders</div>
            </div>
            
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <div className="text-2xl font-bold text-blue-600">{result.processed}</div>
              <div className="text-sm text-gray-600">Successfully Processed</div>
            </div>
            
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <div className="text-2xl font-bold text-orange-600">{result.errors}</div>
              <div className="text-sm text-gray-600">Errors</div>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-white rounded-lg border border-green-200">
            <h4 className="font-medium text-gray-900 mb-2">Summary:</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• {result.total} orders were found in the selected date range</li>
              <li>• {result.processed} orders were successfully validated</li>
              <li>• {result.errors} orders encountered errors during validation</li>
              <li>• All orders with 'INITIATED' status have been updated based on their payment status</li>
            </ul>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="card border-blue-200 bg-blue-50">
        <h3 className="text-lg font-medium text-blue-900 mb-3">How it works</h3>
        <div className="space-y-2 text-sm text-blue-800">
          <p>• Select a date range to validate orders with 'INITIATED' status</p>
          <p>• The system will check each order's payment status with Cashfree</p>
          <p>• Orders with successful payments will be marked as 'SUCCESS'</p>
          <p>• Orders with failed or missing payments will be marked as 'FAILED'</p>
          <p>• This process updates all related tables (payment_orders, nameNumerology_orders, etc.)</p>
        </div>
      </div>
    </div>
  );
};

export default OrderValidation;
